// 订单管理相关API
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  OrderResponse,
  OrderListResponse,
  OrderStatisticsResponse,
  CreateOrderRequest,
  CancelOrderRequest,
  UpdateOrderStatusRequest,
  OrderData,
  OrderListData,
  OrderStatistics
} from './types'

// 查询键工厂
export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (filters: any) => [...orderKeys.lists(), filters] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (id: string) => [...orderKeys.details(), id] as const,
  statistics: (params: any) => [...orderKeys.all, 'statistics', params] as const,
  customerHistory: (customerId: string) => [...orderKeys.all, 'customer', customerId] as const,
  platformHistory: (platform: string, platformId: string) => [...orderKeys.all, 'platform', platform, platformId] as const,
}

// API端点常量
const ORDER_ENDPOINTS = {
  BASE: '/orders',
  health: '/orders/health',
  search: '/orders',
  statistics: '/orders/statistics',
  customerHistory: (customerId: string) => `/orders/customer/${customerId}/history`,
  platformHistory: (platform: string, platformId: string) => `/orders/platform/${platform}/${platformId}/history`,
}

// 健康检查
export const useOrderHealthCheck = (companyId: number = 1) => {
  return useQuery({
    queryKey: [...orderKeys.all, 'health', companyId],
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderApi.healthCheck(companyId)
    },
    retry: false,
    enabled: false, // 需要手动触发
  })
}

// 搜索订单
export interface SearchOrdersParams {
  company_id: number
  customer_id?: string
  status?: string
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
}

export const useSearchOrders = (params: SearchOrdersParams, options: any = {}) => {
  return useQuery<OrderListResponse>({
    queryKey: orderKeys.list(params),
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderApi.searchOrders(params)
    },
    enabled: !!params.company_id,
    ...options,
  })
}

// 获取订单详情
export const useOrderDetail = (orderId: string, companyId: number = 1) => {
  return useQuery({
    queryKey: orderKeys.detail(orderId),
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderApi.getOrder(orderId, companyId)
    },
    enabled: !!orderId,
  })
}

// 创建订单
export const useCreateOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateOrderRequest) => {
      // 使用适配后的API方法
      return await orderApi.createOrder(data)
    },
    onSuccess: (data, variables) => {
      // 使相关查询失效
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: orderKeys.customerHistory(variables.customer_info.email || '')
      })
      queryClient.invalidateQueries({ queryKey: orderKeys.statistics({ company_id: variables.company_id }) })
    },
  })
}

// 取消订单
export const useCancelOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ orderId, ...data }: { orderId: string } & CancelOrderRequest) => {
      // 使用适配后的API方法
      return await orderApi.cancelOrder(orderId, data)
    },
    onSuccess: (data, variables) => {
      // 使相关查询失效
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: orderKeys.statistics({ company_id: variables.company_id })
      })
    },
  })
}

// 更新订单状态
export const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ orderId, ...data }: { orderId: string } & UpdateOrderStatusRequest) => {
      // 使用适配后的API方法
      return await orderApi.updateOrderStatus(orderId, data)
    },
    onSuccess: (data, variables) => {
      // 使相关查询失效
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() })
    },
  })
}

// 获取客户订单历史
export const useCustomerOrderHistory = (customerId: string, companyId: number = 1, limit: number = 20) => {
  return useQuery({
    queryKey: orderKeys.customerHistory(customerId),
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderApi.getCustomerOrderHistory(customerId, companyId, limit)
    },
    enabled: !!customerId,
  })
}

// 获取平台订单历史
export const usePlatformOrderHistory = (platform: string, platformId: string, companyId: number = 1, limit: number = 20) => {
  return useQuery({
    queryKey: orderKeys.platformHistory(platform, platformId),
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderApi.getPlatformOrderHistory(platform, platformId, companyId, limit)
    },
    enabled: !!platform && !!platformId,
  })
}

// 获取订单统计
export interface OrderStatisticsParams {
  company_id: number
  start_date?: string
  end_date?: string
}

export const useOrderStatistics = (params: OrderStatisticsParams) => {
  return useQuery({
    queryKey: orderKeys.statistics(params),
    queryFn: async () => {
      // 使用适配后的API方法
      return await orderApi.getOrderStatistics(params)
    },
    enabled: !!params.company_id,
  })
}

// 直接API调用（不使用React Query）
export const orderApi = {
  // 健康检查
  healthCheck: async (companyId: number = 1): Promise<OrderResponse> => {
    try {
      const response = await api.get<any>(ORDER_ENDPOINTS.health, { params: { company_id: companyId } });

      // 拦截器已剥离外层结构的情况（response是健康数据对象）
      if (response && typeof response === 'object') {
        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderResponse;
        }
        // 否则包装成成功响应
        return {
          success: true,
          data: response,
          error: undefined
        };
      }

      return { success: false, error: "健康检查响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 搜索订单
  searchOrders: async (params: SearchOrdersParams): Promise<OrderListResponse> => {
    try {
      const response = await api.get<any>(ORDER_ENDPOINTS.search, { params });

      // 拦截器已剥离外层结构的情况（response是订单数据对象）
      if (response && typeof response === 'object') {
        // 如果响应有orders字段，说明已经是内部的data对象
        if (response.orders !== undefined) {
          return {
            success: true,
            data: response,  // response已经是{orders, total_count, ...}
            error: undefined
          };
        }

        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderListResponse;
        }
      }

      return { success: false, error: "搜索订单响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 获取订单详情
  getOrder: async (orderId: string, companyId: number = 1): Promise<OrderResponse> => {
    try {
      const response = await api.get<any>(`${ORDER_ENDPOINTS.BASE}/${orderId}`, {
        params: { company_id: companyId }
      });

      // 拦截器已剥离外层结构的情况（response是订单详情对象）
      if (response && typeof response === 'object') {
        // 如果响应有order_id字段，说明已经是订单详情对象
        if (response.order_id !== undefined) {
          return {
            success: true,
            data: response,
            error: undefined
          };
        }

        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderResponse;
        }
      }

      return { success: false, error: "获取订单详情响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 创建订单
  createOrder: async (data: CreateOrderRequest): Promise<OrderResponse> => {
    try {
      const response = await api.post<any>(ORDER_ENDPOINTS.BASE, data);

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应有order_id字段，说明已经是创建的订单对象
        if (response.order_id !== undefined) {
          return {
            success: true,
            data: response,
            error: undefined
          };
        }

        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderResponse;
        }
      }

      return { success: false, error: "创建订单响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 取消订单
  cancelOrder: async (orderId: string, data: CancelOrderRequest): Promise<OrderResponse> => {
    try {
      const response = await api.post<any>(`${ORDER_ENDPOINTS.BASE}/${orderId}/cancel`, data);

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderResponse;
        }
        // 否则包装成成功响应
        return {
          success: true,
          data: response,
          error: undefined
        };
      }

      return { success: false, error: "取消订单响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 更新订单状态
  updateOrderStatus: async (orderId: string, data: UpdateOrderStatusRequest): Promise<OrderResponse> => {
    try {
      const response = await api.put<any>(`${ORDER_ENDPOINTS.BASE}/${orderId}/status`, data);

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderResponse;
        }
        // 否则包装成成功响应
        return {
          success: true,
          data: response,
          error: undefined
        };
      }

      return { success: false, error: "更新订单状态响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 获取客户订单历史
  getCustomerOrderHistory: async (customerId: string, companyId: number = 1, limit: number = 20): Promise<OrderListResponse> => {
    try {
      const response = await api.get<any>(
        ORDER_ENDPOINTS.customerHistory(customerId),
        { params: { company_id: companyId, limit } }
      );

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应有orders字段，说明已经是内部的data对象
        if (response.orders !== undefined) {
          return {
            success: true,
            data: response,  // response已经是{orders, total_count, ...}
            error: undefined
          };
        }

        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderListResponse;
        }
      }

      return { success: false, error: "获取客户订单历史响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 获取平台订单历史
  getPlatformOrderHistory: async (platform: string, platformId: string, companyId: number = 1, limit: number = 20): Promise<OrderListResponse> => {
    try {
      const response = await api.get<any>(
        ORDER_ENDPOINTS.platformHistory(platform, platformId),
        { params: { company_id: companyId, limit } }
      );

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应有orders字段，说明已经是内部的data对象
        if (response.orders !== undefined) {
          return {
            success: true,
            data: response,  // response已经是{orders, total_count, ...}
            error: undefined
          };
        }

        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderListResponse;
        }
      }

      return { success: false, error: "获取平台订单历史响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },

  // 获取订单统计
  getOrderStatistics: async (params: OrderStatisticsParams): Promise<OrderStatisticsResponse> => {
    try {
      const response = await api.get<any>(ORDER_ENDPOINTS.statistics, { params });

      // 拦截器已剥离外层结构的情况
      if (response && typeof response === 'object') {
        // 如果响应有total_orders或total_revenue字段，说明已经是统计对象
        if (response.total_orders !== undefined || response.total_revenue !== undefined) {
          return {
            success: true,
            data: response,
            error: undefined
          };
        }

        // 如果响应已经有success字段，直接返回（完整响应结构）
        if (response.success !== undefined) {
          return response as OrderStatisticsResponse;
        }
      }

      return { success: false, error: "获取订单统计响应格式异常" };
    } catch (error) {
      return { success: false, error: String(error) };
    }
  },
}